import AddItemButton from "@/components/AddItemButton";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { QueryTable } from "@/components/query-table/QueryTable";
import PermissionGuard from "@/components/PermissionGuard";
import Title from "@/components/Title";
import {
  PATH_STRUCTURE_ASSIGNMENT,
  PATH_STRUCTURE_ASSIGNMENT_NEW,
} from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType } from "@/lib/filters";
import { useStructureAssignmentsBreadcrumbs } from "@/modules/structure-assignments/hooks/useStructureAssignmentsBreadcrumbs";
import { StructureAssignmentsService } from "@/modules/structure-assignments/services/structureAssignments";
import {
  ASSIGNMENT_TYPES,
  readStructureAssignment,
} from "@/schemas/structure-assignment";
import { FetchListRequest, SuccessFetchListResponse } from "@/services/api";
import { Operation } from "@/types/permissions";
import { ReadStructureAssignment } from "@/types/structure-assignment";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { Row } from "@tanstack/react-table";
import { exists } from "i18next";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router-dom";

interface StructureAssignmentRow {
  row: Row<ReadStructureAssignment>;
}

export const StructureAssignmentsDirectory = memo(() => {
  const { t } = useTranslation();
  const { assignmentType } = useParams() as {
    assignmentType: ASSIGNMENT_TYPES;
  };

  const name = `structureAssignments-${assignmentType}`;

  const pathBuilder = useCallback(
    (row: ReadStructureAssignment) =>
      generatePath(PATH_STRUCTURE_ASSIGNMENT, {
        supervisorId: row.supervisor_id,
        assignmentType: row.assignment_type,
      }),
    [],
  );

  const columnOverrides = useMemo(
    () => ({
      assignment_type: {
        id: "assignment_type",
        cell: ({ row }: StructureAssignmentRow) => {
          const key = `structureAssignments.enum.assignmentType.${row.original.assignment_type}`;
          if (!exists(key)) {
            return row.original.assignment_type;
          }
          return t(key);
        },
      },
      supervisor_role_code: {
        id: "supervisor_role_code",
        cell: ({ row }: StructureAssignmentRow) => {
          const key = `structureAssignments.enum.supervisorRoleCode.${row.original.supervisor_role_code}`;
          if (!exists(key)) {
            return row.original.supervisor_role_code;
          }
          return t(key);
        },
      },
      subordinate_role_code: {
        id: "subordinate_role_code",
        cell: ({ row }: StructureAssignmentRow) => {
          const key = `structureAssignments.enum.subordinateRoleCode.${row.original.subordinate_role_code}`;
          if (!exists(key)) {
            return row.original.subordinate_role_code;
          }
          return t(key);
        },
      },
    }),
    [t],
  );

  const fetchList = useCallback(
    async (params: FetchListRequest<ReadStructureAssignment>) =>
      StructureAssignmentsService.getList({
        ...params,
        filters: [
          ...(params.filters ?? []),
          {
            id: "assignment_type",
            value: {
              value: assignmentType,
              filter: FilterType.EqualsString,
            },
          },
        ],
      }),
    [assignmentType],
  );

  const options = useCreateDataTableOptions<ReadStructureAssignment>(name);

  const queryKey = useDynamicQueryParams(name, options);

  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      fetchList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [fetchList, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadStructureAssignment>) =>
      lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query }), [query]);

  const breadcrumbs = useStructureAssignmentsBreadcrumbs();

  return (
    <QueryTable
      schema={readStructureAssignment}
      name={name}
      translationKey="structureAssignments"
      pathBuilder={pathBuilder}
      query={query}
      options={options}
      columnOverrides={columnOverrides}
    >
      <QueryTable.Header
        breadcrumbs={breadcrumbs}
        actions={[
          <PermissionGuard permission={Operation.Create} key="add">
            <AddItemButton
              path={PATH_STRUCTURE_ASSIGNMENT_NEW}
              labelKey="structureAssignments.add"
            />
          </PermissionGuard>,
        ]}
      />
      <div className="py-2">
        <Title>{t(`structureAssignments.navigation.${assignmentType}`)}</Title>
      </div>

      <QueryTable.Menubar>
        <QueryTable.Menubar.Tools />
        <QueryTable.Menubar.Columns />
        <QueryTable.Menubar.Reset />
      </QueryTable.Menubar>
      <DataTableQueryContextProvider value={queryValue}>
        <QueryTable.Content>
          <QueryTable.Status />
        </QueryTable.Content>
      </DataTableQueryContextProvider>
    </QueryTable>
  );
});
